import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Clock, Shield, Star, Car, Package, ChevronRight } from 'lucide-react';
import PageSEO from '../components/SEO/PageSEO';
import ModernLandingHeader from '../components/landing/ModernLandingHeader';
import ModernFooter from '../components/landing/ModernFooter';
import { FONT_FAMILY } from '../common/sharedFunctions';
import backgroundImage from '../assets/img/background.jpg';

export default function LandingPage(props) {
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const cartypes = useSelector(state => state.cartypes);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ...rest } = props;
  const [role, setRole] = useState();
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => {
    if (auth?.profile?.uid) {
      setRole(auth.profile.usertype);
      setLoggedIn(true);
    }
  }, [auth]);

  const handleBooking = () => {
    if (loggedIn) {
      navigate(role === 'driver' ? '/bookings' : '/addbookings');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <PageSEO
        title="Book Taxi & Delivery Services Online"
        description="Gruwcer Delivery - Modern delivery platform. Book deliveries instantly, track drivers in real-time. Safe, affordable, and available 24/7."
        keywords="book taxi online, on-demand delivery, ride booking app, cheap taxi, fast delivery, schedule ride, airport taxi, city cab"
        path="/"
      />

      <ModernLandingHeader
        color="transparent"
        changeColorOnScroll={{ height: 400, color: "white" }}
        {...rest}
      />

      {/* Hero Section - Uber style dark with green accent */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img src={backgroundImage} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight" style={{ fontFamily: FONT_FAMILY }}>
                Go anywhere.
                <br />
                <span className="bg-gradient-to-r from-[#009689] to-[#319F44] bg-clip-text text-transparent">Get anything.</span>
              </h1>
              <p className="mt-6 text-lg text-white/70 max-w-lg leading-relaxed" style={{ fontFamily: FONT_FAMILY }}>
                Request a ride, hop in, and relax. Or send packages across the city with reliable delivery — all from one app.
              </p>

              {/* CTA Buttons */}
              <div className="mt-10 flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleBooking}
                  className="flex items-center gap-3 px-8 py-4 bg-white text-black rounded-xl font-bold text-base hover:bg-gray-100 transition-all shadow-2xl"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <Car className="w-5 h-5" />
                  Book a Ride
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleBooking}
                  className="flex items-center gap-3 px-8 py-4 bg-[#009689] text-white rounded-xl font-bold text-base hover:bg-[#007a6e] transition-all shadow-2xl"
                  style={{ fontFamily: FONT_FAMILY }}
                >
                  <Package className="w-5 h-5" />
                  Send a Package
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>

              {/* Stats */}
              <div className="mt-14 flex gap-10">
                {[
                  { value: '10K+', label: 'Happy Riders' },
                  { value: '500+', label: 'Active Drivers' },
                  { value: '50K+', label: 'Trips Completed' },
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <p className="text-3xl font-black text-white" style={{ fontFamily: FONT_FAMILY }}>{stat.value}</p>
                    <p className="text-sm text-white/50 mt-1" style={{ fontFamily: FONT_FAMILY }}>{stat.label}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right - Booking Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-white mb-6" style={{ fontFamily: FONT_FAMILY }}>Where to?</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-4 border border-white/5">
                    <div className="w-3 h-3 rounded-full bg-[#009689]" />
                    <span className="text-white/60 text-sm" style={{ fontFamily: FONT_FAMILY }}>Pickup location</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-4 border border-white/5">
                    <div className="w-3 h-3 rounded-full bg-white" />
                    <span className="text-white/60 text-sm" style={{ fontFamily: FONT_FAMILY }}>Where to?</span>
                  </div>
                  <button
                    onClick={handleBooking}
                    className="w-full py-4 bg-[#009689] hover:bg-[#007a6e] text-white font-bold rounded-xl transition-all text-base"
                    style={{ fontFamily: FONT_FAMILY }}
                  >
                    See prices
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Services Section */}
      <section className="bg-[#0a0a0a] py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight" style={{ fontFamily: FONT_FAMILY }}>
              Two services. <span className="bg-gradient-to-r from-[#009689] to-[#319F44] bg-clip-text text-transparent">One platform.</span>
            </h2>
            <p className="text-white/50 mt-4 text-lg max-w-2xl mx-auto" style={{ fontFamily: FONT_FAMILY }}>
              Whether you need a ride or want to send a package, we've got you covered.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Taxi Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              onClick={handleBooking}
              className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-3xl p-10 hover:border-[#009689]/30 transition-all cursor-pointer overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#009689]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-[#009689]/10 transition-all" />
              <Car className="w-12 h-12 text-[#009689] mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: FONT_FAMILY }}>Taxi Rides</h3>
              <p className="text-white/50 leading-relaxed mb-6" style={{ fontFamily: FONT_FAMILY }}>
                Get picked up in minutes. Choose from Economy, Comfort, or Exclusive vehicles. Real-time tracking included.
              </p>
              <div className="flex items-center gap-2 text-[#009689] font-semibold text-sm group-hover:gap-3 transition-all" style={{ fontFamily: FONT_FAMILY }}>
                Book a ride <ChevronRight className="w-4 h-4" />
              </div>
            </motion.div>

            {/* Delivery Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              onClick={handleBooking}
              className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-3xl p-10 hover:border-[#009689]/30 transition-all cursor-pointer overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#009689]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-[#009689]/10 transition-all" />
              <Package className="w-12 h-12 text-[#009689] mb-6" />
              <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: FONT_FAMILY }}>Delivery</h3>
              <p className="text-white/50 leading-relaxed mb-6" style={{ fontFamily: FONT_FAMILY }}>
                Send packages across the city. From small documents to large parcels. Fast, tracked, and secure.
              </p>
              <div className="flex items-center gap-2 text-[#009689] font-semibold text-sm group-hover:gap-3 transition-all" style={{ fontFamily: FONT_FAMILY }}>
                Send a package <ChevronRight className="w-4 h-4" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vehicle Types */}
      {cartypes.cars && cartypes.cars.length > 0 && (
        <section className="bg-[#111] py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-black text-white mb-12 tracking-tight"
              style={{ fontFamily: FONT_FAMILY }}
            >
              Choose your ride
            </motion.h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cartypes.cars.sort((a, b) => a.pos - b.pos).map((car, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="group bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#009689]/30 hover:bg-white/[0.07] transition-all"
                >
                  <div className="flex items-center justify-between mb-4">
                    <img src={car.image} alt={car.name} className="h-12 w-20 object-contain" />
                    <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-full ${car.category === 'delivery' ? 'bg-[#009689]/20 text-[#009689]' : 'bg-[#009689]/20 text-[#009689]'}`} style={{ fontFamily: FONT_FAMILY }}>
                      {car.category === 'delivery' ? '📦 Delivery' : '🚕 Taxi'}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-white" style={{ fontFamily: FONT_FAMILY }}>{car.name}</h4>
                  <p className="text-sm text-white/40 mt-1 mb-4" style={{ fontFamily: FONT_FAMILY }}>{car.extra_info}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-black text-[#009689]" style={{ fontFamily: FONT_FAMILY }}>
                      {settings?.symbol}{car.rate_per_unit_distance}
                    </span>
                    <span className="text-sm text-white/40" style={{ fontFamily: FONT_FAMILY }}>/{settings?.convert_to_mile ? 'mi' : 'km'}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="bg-[#0a0a0a] py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight" style={{ fontFamily: FONT_FAMILY }}>
              How it works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', icon: MapPin, title: 'Set your location', desc: 'Enter pickup and destination. We\'ll find the nearest drivers.' },
              { step: '2', icon: Car, title: 'Choose & confirm', desc: 'Pick your vehicle type, see the price upfront, and confirm.' },
              { step: '3', icon: Star, title: 'Ride & rate', desc: 'Enjoy your trip with real-time tracking. Rate when done.' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#009689]/10 border border-[#009689]/20 flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-[#009689]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: FONT_FAMILY }}>{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed" style={{ fontFamily: FONT_FAMILY }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="bg-[#009689] py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Shield, title: 'Safe rides', desc: 'Verified drivers, trip sharing, 24/7 support' },
              { icon: Clock, title: 'Always on', desc: 'Available 24/7, rain or shine' },
              { icon: MapPin, title: 'Live tracking', desc: 'Track your driver in real-time on the map' },
              { icon: Star, title: 'Top rated', desc: 'Consistently rated 4.8+ by riders' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <item.icon className="w-8 h-8 mx-auto mb-4 text-white" />
                <h4 className="font-bold text-white text-lg" style={{ fontFamily: FONT_FAMILY }}>{item.title}</h4>
                <p className="text-white/80 text-sm mt-1" style={{ fontFamily: FONT_FAMILY }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0a0a0a] py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight" style={{ fontFamily: FONT_FAMILY }}>
              Ready to ride?
            </h2>
            <p className="text-white/50 mt-4 text-lg" style={{ fontFamily: FONT_FAMILY }}>
              Download the app or book directly from your browser.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button
                onClick={handleBooking}
                className="px-10 py-4 bg-white text-black rounded-xl font-bold text-base hover:bg-gray-100 transition-all"
                style={{ fontFamily: FONT_FAMILY }}
              >
                {loggedIn ? 'Go to Bookings' : 'Get Started'}
              </button>
              {settings?.AppleStoreLink && (
                <a href={settings.AppleStoreLink} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-xl font-bold text-base hover:bg-white/20 transition-all" style={{ fontFamily: FONT_FAMILY }}>
                  App Store
                </a>
              )}
              {settings?.PlayStoreLink && (
                <a href={settings.PlayStoreLink} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-xl font-bold text-base hover:bg-white/20 transition-all" style={{ fontFamily: FONT_FAMILY }}>
                  Google Play
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <ModernFooter />
    </div>
  );
}
